<template>
  <div class="debug-permissions h-screen flex flex-col bg-gray-50 dark:bg-[#13191d] text-gray-800 dark:text-gray-200 font-sans transition-colors duration-300">
    <!-- Header -->
    <header class="bg-white dark:bg-[#13191d] border-b border-gray-200 dark:border-white/5 h-16 flex items-center px-6 flex-shrink-0 shadow-sm z-10 transition-colors duration-300">
      <div class="flex items-center gap-3">
        <div class="w-8 h-8 rounded-sm bg-indigo-50 dark:bg-indigo-500/10 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
          <i class="el-icon-s-check text-lg"></i>
        </div>
        <h1 class="text-lg font-bold text-gray-900 dark:text-white tracking-tight">Permissions & Claims Debugger</h1>
      </div>
    </header>

    <div class="flex flex-1 overflow-hidden">
      <!-- Sidebar: User List -->
      <aside class="w-80 md:w-96 bg-white dark:bg-[#13191d] border-r border-gray-200 dark:border-white/5 flex flex-col flex-shrink-0 z-0 transition-all duration-300">
        <!-- Search Area -->
        <div class="p-4 border-b border-gray-100 dark:border-white/5">
          <div class="relative">
            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <i class="el-icon-search text-gray-400 dark:text-gray-500"></i>
            </div>
            <input
              v-model="userSearch"
              type="text"
              placeholder="Search users..."
              class="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-transparent rounded-sm text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 dark:focus:ring-indigo-500/40 focus:border-indigo-500 dark:focus:border-indigo-500/50 transition-all placeholder-gray-400 dark:placeholder-gray-600"
            />
            <button 
              v-if="userSearch" 
              @click="userSearch = ''" 
              class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <i class="el-icon-circle-close"></i>
            </button>
          </div>
        </div>

        <!-- List -->
        <div class="flex-1 overflow-y-auto custom-scrollbar relative">
          <!-- Loading State -->
          <div v-if="loading" class="absolute inset-0 flex items-center justify-center bg-white/50 dark:bg-black/50 z-10 backdrop-blur-sm">
            <div class="flex flex-col items-center">
               <i class="el-icon-loading text-3xl text-indigo-600 dark:text-indigo-400 mb-2"></i>
               <span class="text-xs font-semibold text-gray-500 dark:text-gray-400 animate-pulse">LOADING USERS...</span>
            </div>
          </div>

          <div v-if="filteredUsers.length === 0 && !loading" class="flex flex-col items-center justify-center h-40 text-gray-400 dark:text-gray-600">
             <i class="el-icon-search text-3xl mb-3 opacity-30"></i>
             <span class="text-sm font-medium">No users found</span>
          </div>

          <ul class="divide-y divide-gray-50 dark:divide-white/5">
            <li
              v-for="user in filteredUsers"
              :key="user.id"
              @click="selectUser(user)"
              class="cursor-pointer transition-all duration-200 group relative"
              :class="isSelected(user) ? 'bg-indigo-50/50 dark:bg-indigo-500/10' : 'hover:bg-gray-50 dark:hover:bg-white/5'"
            >
              <div class="absolute left-0 top-0 bottom-0 w-1 bg-indigo-500 transform transition-transform duration-200" :class="isSelected(user) ? 'scale-y-100' : 'scale-y-0'"></div>
              <div class="p-4 flex items-center gap-3">
                <!-- Avatar -->
                <div 
                  class="w-10 h-10 rounded-sm flex items-center justify-center text-xs font-bold shadow-sm flex-shrink-0 text-white transition-all duration-200" 
                  :style="{ backgroundColor: getUserBrandColor(user.email) }"
                  :class="isSelected(user) ? 'ring-2 ring-offset-2 ring-indigo-500/30 dark:ring-offset-[#13191d]' : 'group-hover:scale-105'"
                >
                  {{ getInitials(user.name) }}
                </div>
                
                <!-- Info -->
                <div class="min-w-0 flex-1">
                  <p class="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate flex items-center gap-2">
                    {{ user.email || 'Unknown User' }}
                    <span v-if="isSelected(user)" class="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse"></span>
                  </p>
                  <p class="text-xs text-gray-500 dark:text-gray-500 truncate font-mono opacity-80 pt-0.5">{{ user.name }}</p>
                </div>
              </div>
            </li>
          </ul>
        </div>
        
        <!-- Sidebar Footer -->
        <div class="p-3 bg-gray-50 dark:bg-[#13191d] border-t border-gray-200 dark:border-white/5 text-[10px] uppercase tracking-wider font-semibold text-gray-400 dark:text-gray-600 text-center">
            {{ filteredUsers.length }} users loaded
        </div>
      </aside>

      <!-- Main Content -->
      <main class="flex-1 overflow-y-auto dark:bg-[#13191d] p-6 md:p-8 relative custom-scrollbar">
        <div v-if="selectedUser" class="max-w-6xl mx-auto space-y-6">
          
          <!-- User Profile Card -->
          <div class="bg-white dark:bg-[#13191d] rounded-sm shadow-sm border border-gray-200 dark:border-white/5 p-6 flex flex-col md:flex-row items-center md:items-start justify-between transition-colors duration-300 gap-6">
            <div class="flex items-center gap-6 w-full md:w-auto">
               <div>
                  <h2 class="text-3xl font-bold text-gray-900 dark:text-white tracking-tight">{{ selectedUser.name }}</h2>
                  <div class="flex items-center gap-2 text-gray-500 dark:text-gray-400 mt-1 font-medium">
                     <i class="el-icon-message text-indigo-500/70"></i>
                     <span>{{ selectedUser.email }}</span>
                  </div>
                  <div class="flex items-center gap-2 text-gray-400 dark:text-gray-500 text-xs mt-3 font-mono bg-black dark:bg-white/5 px-3 py-1.5 rounded-sm w-fit cursor-pointer hover:bg-gray-200 dark:hover:bg-white/10 transition group" @click="copyToClipboard(selectedUser.id)" title="Click to copy ID">
                     <span class="opacity-70 group-hover:opacity-100 transition-opacity">ID:</span>
                     <span class="text-gray-600 dark:text-gray-300">{{ selectedUser.id }}</span>
                     <i class="el-icon-copy-document ml-1 opacity-0 group-hover:opacity-100 transition-opacity"></i>
                  </div>
               </div>
            </div>
            
            <div class="flex gap-3 w-full md:w-auto">
               <div class="flex-1 md:flex-initial bg-orange-50 dark:bg-orange-500/10 rounded-sm p-4 flex flex-col items-center justify-center min-w-[100px] border border-orange-100 dark:border-orange-500/20">
                  <span class="text-2xl font-bold text-orange-600 dark:text-orange-400">{{ userClients.length }}</span>
                  <span class="text-xs font-bold text-orange-400 dark:text-orange-300/60 uppercase tracking-wide mt-1">Clients</span>
               </div>
               <div class="flex-1 md:flex-initial bg-teal-50 dark:bg-teal-500/10 rounded-sm p-4 flex flex-col items-center justify-center min-w-[100px] border border-teal-100 dark:border-teal-500/20">
                  <span class="text-2xl font-bold text-teal-600 dark:text-teal-400">{{ userAccounts.length }}</span>
                  <span class="text-xs font-bold text-teal-400 dark:text-teal-300/60 uppercase tracking-wide mt-1">Accounts</span>
               </div>
               <div class="flex-1 md:flex-initial bg-indigo-50 dark:bg-indigo-500/10 rounded-sm p-4 flex flex-col items-center justify-center min-w-[100px] border border-indigo-100 dark:border-indigo-500/20">
                  <span class="text-2xl font-bold text-indigo-600 dark:text-indigo-400">{{ userClaims.length }}</span>
                  <span class="text-xs font-bold text-indigo-400 dark:text-indigo-300/60 uppercase tracking-wide mt-1">Claims</span>
               </div>
            </div>
          </div>

          <!-- Quick Verification -->
          <div class="bg-white dark:bg-[#13191d] rounded-sm shadow-sm border border-gray-200 dark:border-white/5 overflow-hidden transition-colors duration-300">
             <div class="px-6 py-4 border-b border-gray-100 dark:border-white/5 flex justify-between items-center bg-gray-50/50 dark:bg-white/[0.02]">
                <h3 class="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                  <div class="w-6 h-6 rounded bg-purple-100 dark:bg-purple-500/20 flex items-center justify-center text-purple-600 dark:text-purple-400 text-xs">
                     <i class="el-icon-search"></i>
                  </div>
                  Verify Access
                </h3>
             </div>
             <div class="p-6">
                <div class="flex gap-3">
                   <div class="flex-grow relative group">
                      <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <i class="el-icon-key text-gray-400 group-focus-within:text-indigo-500 transition-colors"></i>
                      </div>
                      <input 
                        v-model="claimToCheck" 
                        type="text"
                        placeholder="Enter claim code (e.g. feature.reports.view)"
                        @keyup.enter="checkClaim"
                        class="w-full pl-10 pr-4 py-3 bg-white dark:bg-[#202024] border border-gray-200 dark:border-[#333] rounded-sm font-mono text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 dark:focus:ring-indigo-500/40 focus:border-indigo-500 dark:focus:border-indigo-500 transition-all shadow-sm"
                      />
                   </div>
                   <button 
                     @click="checkClaim" 
                     :disabled="!claimToCheck"
                     class="px-6 py-2 bg-gray-900 hover:bg-black dark:bg-indigo-600 dark:hover:bg-indigo-500 text-white rounded-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-all font-medium text-sm shadow-md hover:shadow-lg transform active:scale-95"
                   >
                     <span>Check</span>
                     <i class="el-icon-arrow-right font-bold"></i>
                   </button>
                </div>

                <!-- Results Banner (Animated) -->
                <transition>
                   <div v-if="verificationResult !== null" class="mt-6">
                      <div 
                        class="rounded-sm p-5 border flex items-start gap-4 relative overflow-hidden"
                        :class="verificationResult ? 'bg-green-50/50 dark:bg-green-500/5 border-green-200 dark:border-green-500/20' : 'bg-red-50/50 dark:bg-red-500/5 border-red-200 dark:border-red-500/20'"
                      >
                         <div class="absolute top-0 right-0 p-3 opacity-10">
                            <i :class="verificationResult ? 'el-icon-check text-6xl' : 'el-icon-close text-6xl'"></i>
                         </div>
                         
                         <div class="p-2 rounded-full flex-shrink-0 relative z-10" :class="verificationResult ? 'bg-green-100 dark:bg-green-500/20 text-green-600 dark:text-green-400' : 'bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400'">
                           <i :class="verificationResult ? 'el-icon-check text-lg font-bold' : 'el-icon-close text-lg font-bold'"></i>
                         </div>
                         <div class="relative z-10">
                            <h4 class="font-bold text-lg tracking-tight" :class="verificationResult ? 'text-green-800 dark:text-green-400' : 'text-red-800 dark:text-red-400'">
                               {{ verificationResult ? 'Permission Granted' : 'Permission Denied' }}
                            </h4>
                            <p class="mt-1 text-sm opacity-90" :class="verificationResult ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'">
                               The user <strong>{{ verificationResult ? 'HAS' : 'DOES NOT HAVE' }}</strong> the claim:
                            </p>
                            <code class="block mt-3 font-mono text-xs bg-white/60 dark:bg-black/40 px-3 py-1.5 rounded-sm border border-black/5 dark:border-white/10 w-fit text-gray-800 dark:text-gray-200 shadow-sm">
                               {{ lastCheckedClaim }}
                            </code>
                         </div>
                      </div>
                   </div>
                </transition>
             </div>
          </div>

          <!-- Claims Table -->
          <div class="bg-white dark:bg-[#13191d] rounded-sm shadow-sm border border-gray-200 dark:border-white/5 flex flex-col h-auto transition-colors duration-300">
             <div class="px-6 py-4 border-b border-gray-100 dark:border-white/5 flex justify-between items-center bg-gray-50/50 dark:bg-white/[0.02]">
                <div class="flex items-center gap-3">
                   <h3 class="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                     <div class="w-6 h-6 rounded bg-blue-100 dark:bg-blue-500/20 flex items-center justify-center text-blue-600 dark:text-blue-400 text-xs">
                        <i class="el-icon-tickets"></i>
                     </div>
                     Assigned Claims
                   </h3>
                </div>
                
                <div class="relative w-72">
                   <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <i class="el-icon-search text-gray-400 dark:text-gray-500"></i>
                   </div>
                   <input 
                     v-model="claimFilter" 
                     type="text"
                     placeholder="Filter claims..." 
                     class="w-full pl-9 pr-8 py-1.5 bg-white dark:bg-[#202024] border border-gray-200 dark:border-[#333] rounded-sm text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 dark:focus:ring-indigo-500/40 focus:border-indigo-500 dark:focus:border-indigo-500 transition-colors"
                   />
                   <button 
                     v-if="claimFilter" 
                     @click="claimFilter = ''" 
                     class="absolute inset-y-0 right-0 pr-2 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                   >
                     <i class="el-icon-circle-close"></i>
                   </button>
                </div>
             </div>
             
             <div class="flex-1 overflow-auto custom-scrollbar">
                <table class="min-w-full text-left border-collapse">
                   <thead class="sticky top-0 bg-white dark:bg-[#13191d] z-10 shadow-sm ring-1 ring-black/5 dark:ring-white/5">
                      <tr>
                         <th class="px-6 py-3 border-b border-gray-100 dark:border-white/5 text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Claim Code</th>
                         <th class="px-6 py-3 border-b border-gray-100 dark:border-white/5 text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Feature</th>
                         <th class="px-6 py-3 border-b border-gray-100 dark:border-white/5 text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Name</th>
                         <th class="px-6 py-3 border-b border-gray-100 dark:border-white/5 text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Description</th>
                      </tr>
                   </thead>
                   <tbody class="divide-y divide-gray-50 dark:divide-white/5 bg-white dark:bg-[#13191d]">
                      <tr v-if="filteredClaims.length === 0">
                         <td colspan="4" class="px-6 py-12 text-center text-gray-400 dark:text-gray-600 text-sm italic">
                            {{ claimFilter ? 'No claims match your search' : 'No claims found' }}
                         </td>
                      </tr>
                      <tr 
                        v-for="claim in filteredClaims" 
                        :key="claim.id"
                        class="hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors group"
                      >
                         <td class="px-6 py-3 align-top min-w-[280px]">
                            <div class="flex items-center justify-between gap-2">
                               <span 
                                  class="text-xs font-mono px-2 py-1 rounded border break-all transition-colors cursor-help"
                                  :class="getClaimBadgeStyle(claim.code)"
                                  :title="claim.code"
                               >
                                  {{ claim.code }}
                               </span>
                               <button 
                                 class="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 focus:outline-none p-1 rounded-md hover:bg-gray-100 dark:hover:bg-white/10" 
                                 @click="copyToClipboard(claim.code)"
                                 title="Copy code"
                               >
                                  <i class="el-icon-copy-document"></i>
                               </button>
                            </div>
                         </td>
                         <td class="px-6 py-3 align-top">
                            <span v-if="claim.featureCode !== '-'" 
                               class="inline-flex px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border"
                               :class="getFeatureBadgeStyle(claim.featureCode)"
                            >
                               {{ claim.featureCode }}
                            </span>
                            <span v-else class="text-gray-300 dark:text-gray-700 text-xs">-</span>
                         </td>
                         <td class="px-6 py-3 align-top">
                            <span class="font-semibold text-gray-700 dark:text-gray-300 text-sm">{{ claim.name || '-' }}</span>
                         </td>
                         <td class="px-6 py-3 align-top">
                            <span class="text-gray-500 dark:text-gray-400 text-sm leading-snug block max-w-sm">{{ claim.description || '-' }}</span>
                         </td>
                      </tr>
                   </tbody>
                </table>
             </div>
          </div>

          <!-- Clients & Accounts Grid -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <!-- Assigned Clients -->
            <div class="bg-white dark:bg-[#13191d] rounded-sm shadow-sm border border-gray-200 dark:border-white/5 flex flex-col transition-colors duration-300">
               <div class="px-6 py-4 border-b border-gray-100 dark:border-white/5 flex justify-between items-center bg-gray-50/50 dark:bg-white/[0.02]">
                  <h3 class="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                     <div class="w-6 h-6 rounded bg-orange-100 dark:bg-orange-500/20 flex items-center justify-center text-orange-600 dark:text-orange-400 text-xs">
                        <i class="el-icon-office-building"></i>
                     </div>
                     Assigned Clients
                  </h3>
                  <span class="text-xs font-mono bg-gray-100 dark:bg-white/10 px-2 py-0.5 rounded text-gray-500">{{ userClients.length }}</span>
               </div>
               <div class="p-0 h-64 overflow-y-auto custom-scrollbar">
                  <ul v-if="userClients.length" class="divide-y divide-gray-50 dark:divide-white/5">
                     <li v-for="client in userClients" :key="client.id" class="px-6 py-3 hover:bg-gray-50 dark:hover:bg-white/[0.02] flex items-center justify-between group">
                        <div class="flex flex-col">
                           <span class="text-sm font-medium text-gray-900 dark:text-gray-200">{{ client.name }}</span>
                           <span class="text-xs font-mono text-gray-500 dark:text-gray-500">{{ client.code }}</span>
                        </div>
                        <button 
                           class="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 p-1"
                           @click="copyToClipboard(client.id)"
                           title="Copy ID"
                        >
                           <i class="el-icon-copy-document"></i>
                        </button>
                     </li>
                  </ul>
                  <div v-else class="flex flex-col items-center justify-center h-full text-gray-400 text-sm italic p-4">
                     No clients assigned
                  </div>
               </div>
            </div>

            <!-- Assigned Accounts -->
            <div class="bg-white dark:bg-[#13191d] rounded-sm shadow-sm border border-gray-200 dark:border-white/5 flex flex-col transition-colors duration-300">
               <div class="px-6 py-4 border-b border-gray-100 dark:border-white/5 flex justify-between items-center bg-gray-50/50 dark:bg-white/[0.02]">
                  <h3 class="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                     <div class="w-6 h-6 rounded bg-teal-100 dark:bg-teal-500/20 flex items-center justify-center text-teal-600 dark:text-teal-400 text-xs">
                        <i class="el-icon-monitor"></i>
                     </div>
                     Assigned Accounts
                  </h3>
                   <span class="text-xs font-mono bg-gray-100 dark:bg-white/10 px-2 py-0.5 rounded text-gray-500">{{ userAccounts.length }}</span>
               </div>
               <div class="p-0 h-64 overflow-y-auto custom-scrollbar">
                  <ul v-if="userAccounts.length" class="divide-y divide-gray-50 dark:divide-white/5">
                     <li v-for="account in userAccounts" :key="account.id" class="px-6 py-3 hover:bg-gray-50 dark:hover:bg-white/[0.02] flex items-center justify-between group">
                        <div class="flex flex-col">
                           <span class="text-sm font-medium text-gray-900 dark:text-gray-200">{{ account.name }}</span>
                           <span class="text-xs font-mono text-gray-500 dark:text-gray-500">{{ account.code }}</span>
                        </div>
                         <button 
                           class="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 p-1"
                           @click="copyToClipboard(account.id)"
                           title="Copy ID"
                        >
                           <i class="el-icon-copy-document"></i>
                        </button>
                     </li>
                  </ul>
                   <div v-else class="flex flex-col items-center justify-center h-full text-gray-400 text-sm italic p-4">
                     No accounts assigned
                  </div>
               </div>
            </div>
          </div>

          <!-- Raw Debug Data -->
          <div class="bg-white dark:bg-[#13191d] rounded-sm shadow-sm border border-gray-200 dark:border-white/5 overflow-hidden transition-colors duration-300">
             <div 
               class="px-6 py-4 flex justify-between items-center cursor-pointer bg-gray-900 dark:bg-[#0a0a0a] text-white hover:bg-gray-800 dark:hover:bg-black transition-colors"
               @click="showRawPermissions = !showRawPermissions"
             >
                <div class="flex items-center gap-2">
                   <i class="el-icon-cpu text-lg text-gray-400"></i>
                   <h3 class="font-semibold text-sm uppercase tracking-wide">Developer Context / Raw Scopes</h3>
                </div>
                <i :class="showRawPermissions ? 'el-icon-arrow-down' : 'el-icon-arrow-right'" class="transition-transform duration-300" :style="{ transform: showRawPermissions ? 'rotate(180deg)' : 'rotate(0)' }"></i>
             </div>
             
             <transition>
                <div v-if="showRawPermissions" class="p-6 bg-gray-900 dark:bg-[#0a0a0a] border-t border-gray-700 dark:border-gray-800 grid grid-cols-1 lg:grid-cols-2 gap-6">
                   <div class="space-y-2">
                      <div class="flex items-center justify-between">
                         <h4 class="text-gray-500 font-mono text-[10px] uppercase tracking-wider">claimsCanAccess</h4>
                         <span class="bg-white/10 text-gray-300 px-1.5 py-0.5 rounded text-[10px]">{{ selectedUser.claimsCanAccess?.length || 0 }}</span>
                      </div>
                      <div class="bg-black/30 dark:bg-white/5 text-gray-300 p-4 rounded-sm text-xs font-mono h-64 overflow-y-auto border border-white/5 shadow-inner custom-scrollbar-dark ring-1 ring-inset ring-white/5">
                         <div v-for="(item, i) in selectedUser.claimsCanAccess" :key="'acc-'+i" class="mb-1 last:mb-0 hover:text-white transition-colors cursor-default">{{ item }}</div>
                         <div v-if="!selectedUser.claimsCanAccess?.length" class="text-gray-600 italic">No access scopes</div>
                      </div>
                   </div>
                   <div class="space-y-2">
                      <div class="flex items-center justify-between">
                         <h4 class="text-gray-500 font-mono text-[10px] uppercase tracking-wider">claimsCanEdit</h4>
                         <span class="bg-white/10 text-gray-300 px-1.5 py-0.5 rounded text-[10px]">{{ selectedUser.claimsCanEdit?.length || 0 }}</span>
                      </div>
                      <div class="bg-black/30 dark:bg-white/5 text-gray-300 p-4 rounded-sm text-xs font-mono h-64 overflow-y-auto border border-white/5 shadow-inner custom-scrollbar-dark ring-1 ring-inset ring-white/5">
                         <div v-for="(item, i) in selectedUser.claimsCanEdit" :key="'edit-'+i" class="mb-1 last:mb-0 hover:text-white transition-colors cursor-default">{{ item }}</div>
                         <div v-if="!selectedUser.claimsCanEdit?.length" class="text-gray-600 italic">No edit scopes</div>
                      </div>
                   </div>
                </div>
             </transition>
          </div>

        </div>

        <!-- Empty Selection State -->
        <div v-else class="h-full flex flex-col items-center justify-center text-center p-8">
           <div class="w-32 h-32 bg-gray-100 dark:bg-white/5 rounded-full flex items-center justify-center mb-8 transition-colors duration-300 ring-8 ring-gray-50 dark:ring-white/[0.02]">
              <i class="el-icon-user text-5xl text-gray-300 dark:text-gray-600"></i>
           </div>
           <h2 class="text-3xl font-bold text-gray-900 dark:text-white mb-3">Select a User</h2>
           <p class="text-gray-500 dark:text-gray-400 max-w-sm text-lg leading-relaxed">Choose a user from the sidebar to inspect their assigned permissions, verify features, and debug scope access.</p>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';

export default {
  name: 'DebugPermissions',
  data() {
    return {
      userSearch: '',
      selectedUser: null,
      claimToCheck: '',
      lastCheckedClaim: '',
      verificationResult: null,
      claimFilter: '',
      showRawPermissions: false,
      loading: false,
      // Colors for tag generation
      tagColors: [
          { text: 'text-blue-700 dark:text-blue-300', bg: 'bg-blue-50 dark:bg-blue-900/20', border: 'border-blue-200 dark:border-blue-700/30' },
          { text: 'text-purple-700 dark:text-purple-300', bg: 'bg-purple-50 dark:bg-purple-900/20', border: 'border-purple-200 dark:border-purple-700/30' },
          { text: 'text-emerald-700 dark:text-emerald-300', bg: 'bg-emerald-50 dark:bg-emerald-900/20', border: 'border-emerald-200 dark:border-emerald-700/30' },
          { text: 'text-amber-700 dark:text-amber-300', bg: 'bg-amber-50 dark:bg-amber-900/20', border: 'border-amber-200 dark:border-amber-700/30' },
          { text: 'text-rose-700 dark:text-rose-300', bg: 'bg-rose-50 dark:bg-rose-900/20', border: 'border-rose-200 dark:border-rose-700/30' },
          { text: 'text-cyan-700 dark:text-cyan-300', bg: 'bg-cyan-50 dark:bg-cyan-900/20', border: 'border-cyan-200 dark:border-cyan-700/30' },
      ]
    };
  },
  computed: {
    ...mapState({
      userListExtended: state => state.admin.listUsersExtended,
    }),
    users() {
      // Ensure we handle both possible structures if the store structure changes
      const list = this.userListExtended || [];
      return Array.isArray(list) ? list : (list.items || []);
    },
    filteredUsers() {
      if (!this.userSearch) return this.users;
      const search = this.userSearch.toLowerCase();
      return this.users.filter(u => 
        (u.name && u.name.toLowerCase().includes(search)) || 
        (u.email && u.email.toLowerCase().includes(search))
      );
    },
    userClaims() {
      if (!this.selectedUser || !this.selectedUser.claims || !this.selectedUser.claims.items) return [];
      
      return this.selectedUser.claims.items
        .filter(item => item && item.claim) // Filter out nulls
        .map(item => ({
          id: item.claim.id,
          code: item.claim.code,
          name: item.claim.name,
          description: item.claim.description,
          featureCode: item.claim.feature ? item.claim.feature.code : '-',
        }));
    },
    filteredClaims() {
        if (!this.claimFilter) return this.userClaims;
        const search = this.claimFilter.toLowerCase();
        return this.userClaims.filter(c => 
            (c.code && c.code.toLowerCase().includes(search)) ||
            (c.name && c.name.toLowerCase().includes(search)) ||
            (c.featureCode && c.featureCode.toLowerCase().includes(search))
        );
    },
    userClients() {
      if (!this.selectedUser || !this.selectedUser.clients || !this.selectedUser.clients.items) return [];
      return this.selectedUser.clients.items
        .filter(item => item && item.client)
        .map(item => item.client);
    },
    userAccounts() {
      if (!this.selectedUser || !this.selectedUser.accounts || !this.selectedUser.accounts.items) return [];
      return this.selectedUser.accounts.items
        .filter(item => item && item.accounts)
        .map(item => item.accounts); // Field name is 'accounts' although it refers to a single account
    }
  },
  methods: {
    ...mapActions('admin', ['fetchUserListExtended']),
    
    async loadData() {
        this.loading = true;
        try {
            await this.fetchUserListExtended();
        } catch (e) {
            console.error("Failed to load users", e);
            if (this.$message) {
                this.$message.error("Failed to load user list");
            }
        } finally {
            this.loading = false;
        }
    },
    selectUser(user) {
        this.selectedUser = user;
        this.resetVerification();
    },
    isSelected(user) {
        return this.selectedUser && this.selectedUser.id === user.id;
    },
    resetVerification() {
        this.claimToCheck = '';
        this.lastCheckedClaim = '';
        this.verificationResult = null;
        this.claimFilter = '';
        this.showRawPermissions = false;
    },
    checkClaim() {
        if (!this.claimToCheck) return;
        
        const claimCode = this.claimToCheck.trim();
        this.lastCheckedClaim = claimCode;
        
        // Exact match check on code
        const hasClaim = this.userClaims.some(c => c.code === claimCode);
        this.verificationResult = hasClaim;
    },
    // Utils
    getInitials(name) {
        if (!name) return '??';
        const parts = name.split(' ');
        if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    },
    getUserBrandColor(email) {
        // Neutral fallback for simple visual
        if (!email) return '#525252'; 
        let hash = 0;
        for (let i = 0; i < email.length; i++) {
        hash = email.charCodeAt(i) + ((hash << 5) - hash);
        }
        const c = (hash & 0x00ffffff).toString(16).toUpperCase();
        return '#' + '00000'.substring(0, 6 - c.length) + c;
    },
    copyToClipboard(text) {
        if (!text) return;
        navigator.clipboard.writeText(text).then(() => {
            if (this.$message) {
                this.$message.success({
                    message: 'Copied to clipboard',
                    duration: 2000
                });
            }
        }).catch(err => {
            console.error('Could not copy text: ', err);
        });
    },
    // Color generation for tags
    getStringHash(str) {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            hash = str.charCodeAt(i) + ((hash << 5) - hash);
        }
        return Math.abs(hash);
    },
    getFeatureBadgeStyle(featureCode) {
        if (!featureCode || featureCode === '-') return '';
        const index = this.getStringHash(featureCode) % this.tagColors.length;
        const color = this.tagColors[index];
        return `${color.text} ${color.bg} ${color.border}`;
    },
    getClaimBadgeStyle(claimCode) {
        // Use the first part of the claim code (e.g. "feature" in "feature.reports.view") to group colors
        const prefix = claimCode.split('.')[0] || claimCode;
        const index = this.getStringHash(prefix) % this.tagColors.length;
        const color = this.tagColors[index];
        return `${color.text} ${color.bg} ${color.border}`;
    }
  },
  mounted() {
    this.loadData();
  }
};
</script>

<style scoped>
/* Custom Scrollbar */
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: rgba(156, 163, 175, 0.5);
  border-radius: 20px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background-color: rgba(107, 114, 128, 0.8);
}
:global(.dark) .custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.15);
}
:global(.dark) .custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background-color: rgba(255, 255, 255, 0.3);
}

.custom-scrollbar-dark::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar-dark::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 20px;
}

/* Transitions */
.v-enter-active,
.v-leave-active {
  transition: all 0.3s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
